import { useState, useEffect } from "react";
import UserLayout from "../../components/user/dashboard/UserLayout";
import { useUserRentals } from "../../services/user/UserRentalsService";
import {
    Activity,
    ChevronLeft,
    ChevronRight,
    FileText,
    Download,
    RefreshCw,
    Clock,
    ArrowRight
} from "lucide-react";
import { Link } from "react-router-dom";

export default function UserRecentActivityPage() {
    const [activities, setActivities] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
    const [isLoading, setIsLoading] = useState(true);
    const { getUserActivities } = useUserRentals();

    useEffect(() => {
        fetchActivities(1);
    }, []);

    const fetchActivities = async (page) => {
        setIsLoading(true);
        const result = await getUserActivities(page, 10);
        if (result.success) {
            setActivities(result.data);
            setPagination(result.pagination);
        }
        setIsLoading(false);
    };

    const getActivityIcon = (action) => {
        switch (action) {
            case 'rental_requested':
                return <FileText size={18} className="text-blue-500" />;
            case 'status_changed':
                return <RefreshCw size={18} className="text-purple-500" />;
            case 'invoice_downloaded':
                return <Download size={18} className="text-emerald-500" />;
            default:
                return <Activity size={18} className="text-gray-500" />;
        }
    };

    const formatActivityDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <UserLayout>
            <div className="p-4 md:p-8 lg:p-10 max-w-[1000px] mx-auto space-y-8 animate-fade-in pb-20">
                <div>
                    <h1 className="text-2xl font-medium text-[#111111] dark:text-white font-serif">Recent Activity</h1>
                    <p className="text-[#666666] dark:text-[#A0A0A0] text-sm mt-1">Track your interactions and invoice updates</p>
                </div>

                <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm overflow-hidden text-sm">
                    {isLoading ? (
                        <div className="p-12 text-center space-y-4">
                            <div className="animate-spin w-8 h-8 border-4 border-[#62397f] border-t-transparent rounded-full mx-auto"></div>
                            <p className="text-[#666666] dark:text-[#A0A0A0]">Loading activities...</p>
                        </div>
                    ) : activities.length > 0 ? (
                        <div className="divide-y divide-[#E5E7EB] dark:divide-[#2A2A2A]">
                            {activities.map((activity) => (
                                <div key={activity.id} className="p-4 md:p-5 flex gap-4 hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors group">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center border border-[#E5E7EB] dark:border-[#2A2A2A]">
                                        {getActivityIcon(activity.action)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-1 md:gap-4">
                                            <h3 className="font-semibold text-[#111111] dark:text-white truncate uppercase tracking-tight">
                                                {activity.action.replace('_', ' ')}
                                            </h3>
                                            <span className="text-[10px] md:text-xs text-[#9CA3AF] flex items-center gap-1 font-medium bg-gray-50 dark:bg-gray-800 px-2 py-0.5 rounded-full border border-gray-100 dark:border-gray-700">
                                                <Clock size={12} />
                                                {formatActivityDate(activity.created_at)}
                                            </span>
                                        </div>
                                        <p className="text-[#4B5563] dark:text-[#D1D5DB] mt-1 line-clamp-2 md:line-clamp-none">
                                            {activity.details}
                                        </p>

                                        {activity.entity_id && activity.entity_type === 'invoice' && (
                                            <Link
                                                to={`/dashboard/rentals/${activity.entity_id}`}
                                                className="inline-flex items-center gap-1 mt-3 text-[#62397f] hover:text-[#7C3AED] font-bold text-xs uppercase tracking-wider group-hover:translate-x-1 transition-transform"
                                            >
                                                View Invoice <ArrowRight size={14} />
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-20 text-center space-y-4">
                            <div className="w-16 h-16 bg-gray-50 dark:bg-gray-800/50 rounded-full flex items-center justify-center mx-auto text-[#9CA3AF]">
                                <Activity size={32} />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-[#111111] dark:text-white">No activity yet</h3>
                                <p className="text-[#666666] dark:text-[#A0A0A0] text-sm max-w-xs mx-auto mt-1">Your recent actions and updates will appear here.</p>
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    {!isLoading && pagination.pages > 1 && (
                        <div className="p-4 bg-gray-50/50 dark:bg-gray-800/30 border-t border-[#E5E7EB] dark:border-[#2A2A2A] flex items-center justify-between">
                            <p className="text-xs text-[#6B7280] dark:text-[#A0A0A0]">
                                Showing page <span className="font-bold text-[#111111] dark:text-white">{pagination.page}</span> of <span className="font-bold text-[#111111] dark:text-white">{pagination.pages}</span>
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => fetchActivities(pagination.page - 1)}
                                    disabled={pagination.page <= 1}
                                    className="p-2 rounded-lg border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#1A1A1A] disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-[#262626] transition-colors"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <button
                                    onClick={() => fetchActivities(pagination.page + 1)}
                                    disabled={pagination.page >= pagination.pages}
                                    className="p-2 rounded-lg border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#1A1A1A] disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-[#262626] transition-colors"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <style jsx="true">{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
      `}</style>
        </UserLayout>
    );
}
